/**
 * Error types and utilities for consistent error handling
 */

export type ErrorType =
    | 'auth'
    | 'permission'
    | 'notfound'
    | 'server'
    | 'network'
    | 'validation'

export interface AppError {
    type: ErrorType
    statusCode: number
    message: string
    context?: string
    originalError?: Error
}

export const ERROR_CONFIG = {
    auth: {
        statusCode: 401,
        title: '401 - Unauthorized',
        description: 'You need to be authenticated to access this resource.',
        showAuth: true,
        showRefresh: false,
    },
    permission: {
        statusCode: 403,
        title: '403 - Forbidden',
        description: "You don't have permission to access this resource.",
        showAuth: true,
        showRefresh: false,
    },
    notfound: {
        statusCode: 404,
        title: '404 - Page Not Found',
        description:
            "The page you're looking for doesn't exist or has been moved.",
        showAuth: true,
        showRefresh: false,
    },
    server: {
        statusCode: 500,
        title: '500 - Server Error',
        description:
            "Something went wrong on our end. We're working to fix it.",
        showAuth: false,
        showRefresh: true,
    },
    network: {
        statusCode: 503,
        title: '503 - Service Unavailable',
        description:
            'The service is temporarily unavailable. Please try again later.',
        showAuth: false,
        showRefresh: true,
    },
    validation: {
        statusCode: 400,
        title: '400 - Bad Request',
        description:
            'The request could not be understood by the server due to malformed syntax.',
        showAuth: false,
        showRefresh: true,
    },
} as const

/**
 * Creates a standardized error object
 */
export const createError = (
    type: ErrorType,
    message?: string,
    context?: string,
    originalError?: Error,
): AppError => ({
    type,
    statusCode: ERROR_CONFIG[type].statusCode,
    message: message || ERROR_CONFIG[type].description,
    context,
    originalError,
})

/**
 * Extracts error information from various error sources
 */
export const parseError = (error: any, context?: string): AppError => {
    // Handle errors with attached appError data (from useErrorHandler)
    if (error?.appError) {
        return {
            ...error.appError,
            context: error.appError.context || context,
        }
    }

    // Handle known error types
    if (error?.type && ERROR_CONFIG[error.type as ErrorType]) {
        return error as AppError
    }

    // Handle HTTP errors
    if (error?.response?.status) {
        const status = error.response.status
        if (status === 401)
            return createError(
                'auth',
                error.response.data?.message,
                context,
                error,
            )
        if (status === 403)
            return createError(
                'permission',
                error.response.data?.message,
                context,
                error,
            )
        if (status === 404)
            return createError(
                'notfound',
                error.response.data?.message,
                context,
                error,
            )
        if (status >= 500)
            return createError(
                'server',
                error.response.data?.message,
                context,
                error,
            )
        return createError(
            'validation',
            error.response.data?.message,
            context,
            error,
        )
    }

    // Handle network errors
    if (
        error?.code === 'NETWORK_ERROR' ||
        error?.message?.includes('Network Error')
    ) {
        return createError('network', error.message, context, error)
    }

    // Handle JavaScript errors
    if (error instanceof Error) {
        return createError('server', error.message, context, error)
    }

    // Fallback
    return createError('server', 'An unexpected error occurred', context, error)
}
